import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
// eslint-disable-next-line import/extensions
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './styles.scss';
import cn from 'classnames';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
// eslint-disable-next-line import/extensions
import 'swiper/css';

export const DateCarousel = ({ dateTimeEntries, selectedOccurrence, onClickOccurrence, pagination, onLoadMore }) => {
  const swiperRef = useRef();
  const [isLeftButtonDisabled, setIsLeftButtonDisabled] = useState(true);
  const [isRightButtonDisabled, setIsRightButtonDisabled] = useState(false);
  const loadingTriggeredRef = useRef(false);

  // Calculate if we can slide right based on current swiper state
  const canSlideRight = useCallback(
    (swiper) => {
      if (!swiper) return false;

      const totalSlides = dateTimeEntries.size;
      const slidesPerView = swiper.params.slidesPerView === 'auto' ? swiper.slidesPerViewDynamic() : swiper.params.slidesPerView;
      const { activeIndex } = swiper;

      // Check if there are more slides beyond the current view
      // We use a small buffer (0.3) to account for partially visible slides
      return activeIndex + slidesPerView < totalSlides + 0.3;
    },
    [dateTimeEntries.size]
  );

  // Infinite scroll detection - trigger if ANY empty space is visible
  const handleScrollProgress = useCallback(
    (swiper) => {
      // Prevent duplicate triggers
      if (loadingTriggeredRef.current || pagination?.loading || !pagination?.hasMore) {
        return;
      }

      // Use actual date count (excluding loading indicator) for calculation
      const actualDateCount = dateTimeEntries.size;
      const slidesPerView = swiper.params.slidesPerView === 'auto' ? swiper.slidesPerViewDynamic() : swiper.params.slidesPerView;

      // Calculate how many slides are beyond the current viewport
      const remainingSlidesNotVisible = actualDateCount - (swiper.activeIndex + slidesPerView);

      // Load more if we have 3 or fewer remaining slides
      // This keeps carousel full and loads proactively
      if (remainingSlidesNotVisible > 0) {
        loadingTriggeredRef.current = true;
        onLoadMore();

        // Reset trigger after delay
        setTimeout(() => {
          loadingTriggeredRef.current = false;
        }, 300);
      }
    },
    [pagination?.hasMore, pagination?.loading, onLoadMore, dateTimeEntries.size]
  );

  const checkSwiperState = useCallback(
    (swiper) => {
      setIsLeftButtonDisabled(swiper.isBeginning);

      // Disable right button only if we can't slide right AND no more data to load
      const canSlide = canSlideRight(swiper);
      setIsRightButtonDisabled(!canSlide && !pagination?.hasMore);
    },
    [canSlideRight, pagination?.hasMore]
  );

  const onBeforeInit = (swiper) => {
    swiperRef.current = swiper;
    checkSwiperState(swiper);
  };

  const handleOnTransitionSlide = useCallback(
    (swiper) => {
      checkSwiperState(swiper);
      handleScrollProgress(swiper);
    },
    [handleScrollProgress]
  );

  const handleSlideChange = useCallback(
    (swiper) => {
      checkSwiperState(swiper);
      handleScrollProgress(swiper);
    },
    [handleScrollProgress]
  );

  const handleReachEnd = useCallback(() => {
    if (!loadingTriggeredRef.current && pagination?.hasMore && !pagination?.loading) {
      loadingTriggeredRef.current = true;
      onLoadMore();
      setTimeout(() => {
        loadingTriggeredRef.current = false;
      }, 300);
    }
  }, [pagination?.hasMore, pagination?.loading, onLoadMore]);

  const handleLeftClick = useCallback(() => {
    if (swiperRef.current && !swiperRef.current.isBeginning) {
      swiperRef.current.slidePrev();
    }
  }, []);

  const handleRightClick = useCallback(() => {
    if (!swiperRef.current) return;

    const swiper = swiperRef.current;

    // Check if we can still slide right to reveal more dates
    if (canSlideRight(swiper)) {
      // Navigate to next slide
      swiper.slideNext();
      // Check if we need to load more after clicking
      setTimeout(() => {
        if (swiperRef.current) {
          handleScrollProgress(swiperRef.current);
        }
      }, 100);
    } else if (pagination?.hasMore && !pagination?.loading && !loadingTriggeredRef.current) {
      // At the end but more data exists, trigger load
      loadingTriggeredRef.current = true;
      onLoadMore();
      setTimeout(() => {
        loadingTriggeredRef.current = false;
      }, 300);
    }
  }, [canSlideRight, handleScrollProgress, pagination?.hasMore, pagination?.loading, onLoadMore]);

  // Auto-fill: Load more dates if carousel has empty space
  useEffect(() => {
    if (!swiperRef.current || !pagination || !onLoadMore) {
      return undefined;
    }

    // Wait a tick for swiper to update after data changes
    const timer = setTimeout(() => {
      const swiper = swiperRef.current;

      if (!swiper || !pagination.hasMore || pagination.loading || loadingTriggeredRef.current) {
        return;
      }

      // Get the number of actual date slides (excluding loading indicator)
      const actualDateCount = dateTimeEntries.size;

      // Get how many slides can fit on screen
      // Use the breakpoint-aware slidesPerView value
      const slidesPerView = swiper.params.slidesPerView === 'auto' ? swiper.slidesPerViewDynamic() : swiper.params.slidesPerView;

      // If we have fewer dates than can fit on screen with a small buffer, load more
      // Buffer of 1 ensures we always have at least one date beyond the visible area
      if (actualDateCount <= slidesPerView + 1) {
        loadingTriggeredRef.current = true;
        onLoadMore();

        // Reset trigger after delay
        setTimeout(() => {
          loadingTriggeredRef.current = false;
        }, 300);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [dateTimeEntries.size, pagination?.hasMore, pagination?.loading, pagination?.returned, onLoadMore]);

  const slides = useMemo(() => {
    const dateSlides = dateTimeEntries
      .map((date) => (
        <SwiperSlide
          key={date.id}
          className={cn(styles.swiperSlide, { [styles.selected]: date.id === selectedOccurrence })}
          onClick={() => onClickOccurrence(date.id)}
        >
          <div className={styles.week}>{date.start.format('ddd')}</div>
          <div className={styles.day}>{date.start.format('D MMM')}</div>
        </SwiperSlide>
      ))
      .toArray();

    // Add loading indicator at the end
    if (pagination?.hasMore || pagination?.loading) {
      dateSlides.push(
        <SwiperSlide key="loading" className={cn(styles.swiperSlide, styles.loadingSlide)}>
          <div className={styles.spinner}>...</div>
        </SwiperSlide>
      );
    }

    return dateSlides;
  }, [selectedOccurrence, dateTimeEntries, pagination?.hasMore, pagination?.loading, onClickOccurrence]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.swiperContainer}>
        {!isLeftButtonDisabled && (
          <div className={styles.buttonLeft} onClick={handleLeftClick}>
            <ArrowBackIosIcon className={styles.icon} />
          </div>
        )}
        <Swiper
          spaceBetween={8}
          slidesPerView="auto"
          onTransitionStart={handleOnTransitionSlide}
          onSlideChange={handleSlideChange}
          onReachEnd={handleReachEnd}
          onBeforeInit={onBeforeInit}
          className={styles.dateTimeCarousel}
          touchRatio={1}
          simulateTouch
          allowTouchMove
          grabCursor
          breakpoints={{
            0: {
              slidesPerView: 1,
            },
            300: {
              slidesPerView: 1,
            },
            480: {
              slidesPerView: 4,
            },
            768: {
              slidesPerView: 5,
            },
          }}
        >
          {slides}
        </Swiper>
        {!isRightButtonDisabled && (
          <div className={styles.buttonRight} onClick={handleRightClick}>
            <ArrowForwardIosIcon className={styles.icon} />
          </div>
        )}
      </div>

      {pagination?.error && (
        <div className={styles.error}>
          {pagination.error}
          <button onClick={onLoadMore}>Retry</button>
        </div>
      )}
    </div>
  );
};
